import React, { useEffect, useRef, useState } from "react";
import Highcharts from "highcharts";
import { useHistory } from "react-router-dom";
import { apiService } from "lib/apiService";

const StackedBarCategoryChart = ({ years, currency }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const history = useHistory();

  // Redirect if user not logged in
  useEffect(() => {
    if (!localStorage.getItem("userData")) {
      history.push("/login");
    }
  }, [history]);

  // Get groupId from localStorage
  const storedUserData = localStorage.getItem("userData");
  let groupId = "";
  if (storedUserData) {
    const userData = JSON.parse(storedUserData);
    groupId = userData.groupId;
  }

  // Detect dark mode
  useEffect(() => {
    const checkDarkMode = () =>
      document.documentElement.classList.contains("dark");

    setIsDarkMode(checkDarkMode());

    const observer = new MutationObserver(() => setIsDarkMode(checkDarkMode()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  // Fetch data and render chart
  useEffect(() => {
    if (!groupId) return;

    const fetchDataAndRenderChart = async () => {
      try {
        const { data, categories } = await apiService.getYearlyCategoryExpenses(
          groupId,
          years
        );

        data.sort((a, b) => b.year - a.year);

        const categoryTotals = {};
        categories.forEach((category) => {
          categoryTotals[category] = 0;
          data.forEach((yearData) => {
            categoryTotals[category] += yearData[category] ? Number(yearData[category]) : 0;
          });
        });

        const latestYearIndex = data.length - 1;

        const sortedCategories = categories
          .filter((c) => categoryTotals[c] > 0)
          .sort((a, b) => {
            const valA = data[latestYearIndex][a] ? Number(data[latestYearIndex][a]) : 0;
            const valB = data[latestYearIndex][b] ? Number(data[latestYearIndex][b]) : 0;
            return valB - valA;   // sort using latest year's values
          });

        const series = sortedCategories.map((category) => ({
          name: category,
          data: data.map((yearData) =>
            yearData[category] ? Number(yearData[category]) : null
          ),
        }));

        const yearLabels = data.map((item) => item.year);

        if (chartContainerRef.current) {
          if (chartRef.current) chartRef.current.destroy();

          chartRef.current = Highcharts.chart(chartContainerRef.current, {
            chart: {
              type: "bar",
              height: 450,
              backgroundColor: "transparent",
            },
            title: { text: undefined },
            xAxis: { categories: yearLabels, crosshair: true },
            yAxis: {
              min: 0,
              title: { text: `Amount (${currency})` },
              labels: {
                formatter: function () {
                  const value = this.value;
                  if (value >= 1000000) return currency + (value / 1000000).toFixed(1) + 'M';
                  if (value >= 1000) return currency + (value / 1000).toFixed(0) + 'k';
                  return currency + value;
                },
              },
            },
            tooltip: {
              shared: true,
              useHTML: true,
              backgroundColor: isDarkMode ? "#111827" : "#ffffff",
              style: {
                color: isDarkMode ? "#f3f4f6" : "#111827",
                padding: "10px",
                fontSize: "13px",
              },
              formatter: function () {
                const year = this.points?.[0]?.key;
                let total = 0;

                let html = `<b>${year}</b><br/>`;

                this.points.forEach((p) => {
                  const value = Number(p.y || 0);
                  total += value;

                  const formatted = value.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });

                  html += `
        <span style="color:${p.color}">●</span>
        ${p.series.name}: <b>${currency}${formatted}</b><br/>
      `;
                });

                const totalFormatted = total.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });

                html += `
      <hr style="
        border: none;
        border-top: 1px solid ${isDarkMode ? "#374151" : "#e5e7eb"};
        margin: 6px 0;
      "/>
      <b>Total: ${currency}${totalFormatted}</b>
    `;

                return html;
              },
            },
            legend: {
              itemStyle: {
                color: isDarkMode ? "#f3f4f6" : "#111827",
                fontSize: "12px",
              },
            },
            plotOptions: {
              bar: {
                stacking: "normal",
                minPointLength: 35,
                dataLabels: {
                  enabled: true,
                  formatter: function () {
                    const val = this.y;
                    if (val >= 1000000) return currency + (val / 1000000).toFixed(2) + 'M';
                    if (val >= 1000) return currency + (val / 1000).toFixed(2) + 'k';
                    return currency + val;
                  },
                  color: isDarkMode ? "#111" : "#111",
                  style: { textOutline: "none", fontSize: "10px" },
                },
              },
            },
            credits: { enabled: false },
            series: series,
            colors: [
              "#ffc107",
              // "#51291e",
              "#95c623",
              // "#6610f2",
              // "#ff758f",
              "#20c997",
              "#d00000",
              "#9d4edd",
              "#f77f00",
              "#4c9bfd",
              "#0dcaf0",
              "#ff4d6d",
            ],
          });
        }
      } catch (error) {
        console.error("Chart Load Error:", error);
        if (chartContainerRef.current) {
          chartContainerRef.current.innerHTML = `
            <div class="flex items-center justify-center text-red-500 h-full">
              Failed to load chart data
            </div>`;
        }
      }
    };

    fetchDataAndRenderChart();
  }, [groupId, years, isDarkMode, currency]);

  return (
    <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-4 w-full">
      <h2 className="text-base font-semibold text-gray-800 dark:text-white mb-3">
        Yearly Debit by Category
      </h2>
      <div style={{ width: "100%", overflowX: "auto" }}>
        <div style={{ minWidth: "800px" }}>
          <div ref={chartContainerRef} style={{ height: 430 }} />
        </div>
      </div>
    </div>
  );
};

export default StackedBarCategoryChart;